export type JsonObject = Record<string, unknown>;

export function unwrapApiData<T>(response: unknown): T {
  const rawData = (response as { data?: unknown })?.data;
  if (
    rawData &&
    typeof rawData === "object" &&
    "data" in (rawData as Record<string, unknown>)
  ) {
    return (rawData as { data: T }).data;
  }
  return rawData as T;
}

export function getErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") return "Request failed.";
  const payload = error as {
    response?: { data?: { error?: string } };
    message?: string;
  };
  return payload.response?.data?.error || payload.message || "Request failed.";
}

export function parseJsonObject(input: string, fieldName: string): JsonObject {
  try {
    const parsed = JSON.parse(input);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error(`${fieldName} must be a JSON object.`);
    }
    return parsed as JsonObject;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : `${fieldName} must be valid JSON.`,
    );
  }
}

export function parseJsonArray(input: string, fieldName: string): unknown[] {
  try {
    const parsed = JSON.parse(input);
    if (!Array.isArray(parsed)) {
      throw new Error(`${fieldName} must be a JSON array.`);
    }
    return parsed;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : `${fieldName} must be valid JSON array.`,
    );
  }
}

export function toDateTimeLabel(value?: string | null): string {
  if (!value) return "N/A";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export function validateTrendThresholds(thresholds: Record<string, number>): string | null {
  const checks: Array<[keyof typeof thresholds, string, number, number]> = [
    ["dsa_low", "DSA lower bound", 0, 100], ["dsa_high", "DSA upper bound", 0, 100],
    ["csa_threshold", "CSA threshold", 0, 100], ["asymmetry_pct", "Asymmetry threshold", 0, 100],
    ["lookback_seconds", "Lookback window", 1, 86400], ["lookahead_seconds", "Lookahead window", 1, 86400],
    ["evaluation_interval_seconds", "Evaluation interval", 1, 3600],
  ];
  for (const [key, label, min, max] of checks) {
    const value = thresholds[key];
    if (!Number.isFinite(value) || value < min || value > max) return `${label} must be between ${min} and ${max}.`;
  }
  return thresholds.dsa_low > thresholds.dsa_high ? "DSA lower bound must not exceed the upper bound." : null;
}
